import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBankAccountStore } from '@/stores/bankAccountStore';
import { useBankLogo } from '@/hooks/useBankLogo';
import Icon from '../components/Icon';
import BankLogo from '../components/BankLogo';

export const BankAccountsPage = () => {
  const navigate = useNavigate();
  const {
    accounts,
    isLoading,
    error,
    balanceTotals,
    fetchAccounts,
    syncingAccountIds,
    syncProgress,
    syncAccount,
    initiateConsent,
    setSearchFilters,
    clearError,
  } = useBankAccountStore();

  const logoFor = useBankLogo();

  const [searchString, setSearchString] = useState('');
  // Account whose consent request is in flight. A double-click used to create two consents
  // for the same IBAN seconds apart; SIBS stores both in one row (the second overwrites the
  // first) and banks may invalidate overlapping consents for the same PSU/account.
  const [initiatingConsentFor, setInitiatingConsentFor] = useState<number | null>(null);
  const initialized = useRef(false);

  // Fetch accounts from database on mount (only once)
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    fetchAccounts();
  }, []);

  const getConsentStatus = (account: { consentExpiresAt: string }) => {
    // C# default DateTime may serialize with or without the trailing 'Z'.
    if (!account.consentExpiresAt || account.consentExpiresAt.startsWith('0001-01-01')) {
      return { state: 'missing' as const, daysLeft: 0 };
    }
    const now = new Date();
    const expires = new Date(account.consentExpiresAt);
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return { state: 'expired' as const, daysLeft: 0 };
    if (daysLeft <= 30) return { state: 'expiring' as const, daysLeft };
    return { state: 'valid' as const, daysLeft };
  };

  const handleProvideConsent = async (e: React.MouseEvent, accountId: number) => {
    e.stopPropagation();
    if (initiatingConsentFor !== null) return;
    setInitiatingConsentFor(accountId);
    const redirectUrl = await initiateConsent(accountId);
    if (redirectUrl) {
      // Keep the button locked while the browser navigates to the bank.
      window.location.href = redirectUrl;
      return;
    }
    setInitiatingConsentFor(null);
  };

  const handleSyncAccount = (e: React.MouseEvent, accountId: number) => {
    e.stopPropagation();
    syncAccount(accountId);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    setSearchFilters({ searchString: value });
  };

  const handleAddAccount = () => {
    navigate('/accounts/add');
  };

  const handleAccountClick = (accountId: number) => {
    navigate(`/accounts/${accountId}`);
  };

  // A code Intl does not know (a bad bank value) shows as "1,234.56 ABCD" rather than breaking the page.
  const formatCurrency = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch {
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
    }
  };

  const accountTotals = balanceTotals.filter((t) => !t.isCard);
  const cardTotals = balanceTotals.filter((t) => t.isCard);
  // With one currency the figure speaks for itself, as before; counts only tell several apart.
  const showCounts = accountTotals.length > 1;

  const formatDate = (dateString: string) => {
    // Handle null, empty, or C# default DateTime (0001-01-01)
    if (!dateString || dateString === '0001-01-01T00:00:00' || dateString.startsWith('0001-01-01')) {
      return 'Never';
    }

    const date = new Date(dateString);

    // Handle invalid dates
    if (isNaN(date.getTime())) {
      return 'Never';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  };

  if (isLoading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600 dark:text-slate-400">
          Loading bank accounts...
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-10">
      <header className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Bank Accounts</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage your synchronized bank accounts.
            </p>
          </div>
        </div>
      </header>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="error" className="text-red-600 dark:text-red-400" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
          <button
            onClick={clearError}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <Icon name="close" />
          </button>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            className="w-full rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:border-primary focus:ring-primary dark:text-slate-300 pl-10 pr-4 py-2.5 text-sm"
            placeholder="Search by bank name, friendly name, or IBAN..."
            type="text"
            value={searchString}
            onChange={handleSearch}
          />
        </div>
        <button
          onClick={handleAddAccount}
          className="flex items-center gap-2 bg-primary text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:ring-offset-background-light dark:focus:ring-offset-background-dark transition-all"
        >
          <Icon name="add" size={22} />
          Add Account
        </button>
      </div>

      {/* One total per currency: balances in different currencies never add up to one figure. */}
      {balanceTotals.length > 0 && (
        <div className="mb-6 bg-primary/10 dark:bg-primary/20 rounded-lg p-4" data-testid="balance-totals">
          <p className="text-sm text-slate-600 dark:text-slate-400">Total Balance</p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-1">
            {accountTotals.map((total) => (
              <div key={total.currency} data-testid={`balance-total-${total.currency}`}>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(total.balance, total.currency)}
                </p>
                {showCounts && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {total.accountCount} {total.accountCount === 1 ? 'account' : 'accounts'}
                  </p>
                )}
              </div>
            ))}
          </div>
          {cardTotals.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mt-3 pt-3 border-t border-primary/20">
              <span className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                <Icon name="credit_card" size={16} />
                Cards
              </span>
              {cardTotals.map((total) => (
                <span
                  key={total.currency}
                  className="text-lg font-semibold text-slate-900 dark:text-white"
                  data-testid={`card-total-${total.currency}`}
                >
                  {formatCurrency(total.balance, total.currency)}
                  <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                    ({total.accountCount} {total.accountCount === 1 ? 'card' : 'cards'})
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* On a phone the table scrolls sideways inside its card rather than squeezing its columns. */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th className="p-4 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider w-2/5">
                Account
              </th>
              <th className="p-4 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider w-2/5">
                IBAN
              </th>
              <th className="p-4 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider w-1/5 text-right">
                Balance
              </th>
              <th className="p-4 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider w-[180px] text-center">
                Consent
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {accounts.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-500 dark:text-slate-400">
                  No bank accounts found. Click "Add Account" to connect your first bank account.
                </td>
              </tr>
            ) : (
              accounts.map((account) => (
                <tr
                  key={account.id}
                  onClick={() => handleAccountClick(account.id)}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <BankLogo
                        {...logoFor(account)}
                        name={account.bankName}
                        className="h-10 w-10 p-1 rounded-full border border-slate-200 dark:border-slate-700"
                      />
                      <div>
                        <div className="font-semibold text-slate-800 dark:text-slate-200">
                          {account.friendlyName}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {account.bankName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {account.type === 'Credit' ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">
                          <Icon name="credit_card" size={14} />
                          Card
                        </span>
                        <span className="font-mono text-sm text-slate-500 dark:text-slate-400">
                          {account.maskedPan || '•••• ••••'}
                        </span>
                      </div>
                    ) : (
                      <div className="font-mono text-sm text-slate-500 dark:text-slate-400">
                        {account.iban}
                      </div>
                    )}
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      Last synced {formatDate(account.lastSyncedAt)}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <div
                      className={`font-semibold text-lg ${
                        account.balance < 0
                          ? 'text-red-500 dark:text-red-400'
                          : 'text-slate-800 dark:text-slate-200'
                      }`}
                    >
                      {formatCurrency(account.balance, account.currency)}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    {(() => {
                      // LusoPay connects via stored credentials (never expires) instead of consent.
                      if (account.integrationType === 'LusoPay') {
                        if (!account.lusopayCredentialsSet) {
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/accounts/${account.id}?setup=lusopay`);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                            >
                              <Icon name="key" size={16} />
                              Add credentials
                            </button>
                          );
                        }
                        return (
                          <div className="flex items-center justify-center gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 rounded-full">
                              <Icon name="verified_user" size={16} />
                              Connected
                            </span>
                            {account.lastSyncError && (
                              <span
                                className="inline-flex items-center text-amber-600 dark:text-amber-400"
                                title={`Last sync failed: ${account.lastSyncError}`}
                              >
                                <Icon name="warning" size={16} />
                              </span>
                            )}
                            {syncingAccountIds.includes(account.id) ? (
                              <span className="animate-pulse text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full">
                                {syncProgress[account.id] ? `Syncing · ${syncProgress[account.id].toLocaleString()}` : 'Syncing...'}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => handleSyncAccount(e, account.id)}
                                className="inline-flex items-center p-1 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                                title={account.lastSyncError ? 'Retry sync' : 'Sync account'}
                              >
                                <Icon name="sync" size={16} />
                              </button>
                            )}
                          </div>
                        );
                      }
                      // OpenBanking consent only applies to SIBS-integrated banks and Revolut.
                      // Other own-integrations (PayPal/Stripe/…) have no connection action here.
                      if (account.integrationType !== 'Sibs' && account.integrationType !== 'Revolut') {
                        return (
                          <span
                            className="text-xs text-slate-400 dark:text-slate-500"
                            title="OpenBanking consent is not available for this bank"
                          >
                            —
                          </span>
                        );
                      }
                      const consent = getConsentStatus(account);
                      if (consent.state === 'missing' || consent.state === 'expired') {
                        return (
                          <button
                            onClick={(e) => handleProvideConsent(e, account.id)}
                            disabled={initiatingConsentFor !== null}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon name="gpp_bad" size={16} />
                            Provide Consent
                          </button>
                        );
                      }
                      if (consent.state === 'expiring') {
                        return (
                          <button
                            onClick={(e) => handleProvideConsent(e, account.id)}
                            disabled={initiatingConsentFor !== null}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                          >
                            <Icon name="schedule" size={16} />
                            Refresh Consent
                          </button>
                        );
                      }
                      // Consent is valid → Connected. A sync failure does NOT revoke consent;
                      // surface it as a warning with a Retry (the sync button), no re-consent.
                      return (
                        <div className="flex items-center justify-center gap-1">
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 rounded-full">
                            <Icon name="verified_user" size={16} />
                            {consent.daysLeft}d
                          </span>
                          {account.lastSyncError && (
                            <span
                              className="inline-flex items-center text-amber-600 dark:text-amber-400"
                              title={`Last sync failed: ${account.lastSyncError}`}
                            >
                              <Icon name="warning" size={16} />
                            </span>
                          )}
                          {syncingAccountIds.includes(account.id) ? (
                            <span className="animate-pulse text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full">
                              {syncProgress[account.id] ? `Syncing · ${syncProgress[account.id].toLocaleString()}` : 'Syncing...'}
                            </span>
                          ) : (
                            <button
                              onClick={(e) => handleSyncAccount(e, account.id)}
                              className="inline-flex items-center p-1 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                              title={account.lastSyncError ? 'Retry sync' : 'Sync account'}
                            >
                              <Icon name="sync" size={16} />
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
